import { LeadEngagements } from "@/components/LeadEngagements";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Badge, Button, Card, Empty, ErrorNote, Field, inputClass, Spinner } from "@/components/ui";
import { AgentSuggestion } from "@/components/AgentSuggestion";
import { AutoAppliedPanel } from "@/components/AutoApplied";
import { CurativeCard } from "@/components/CurativeCard";
import { EditableFacts } from "@/components/EditableFacts";
import { LeadNav } from "@/components/LeadNav";
import { DdSnapshotTab } from "@/components/DdSnapshot";
import { CallLogPanel, OutcomePill } from "@/components/CallLog";
import { CallsPanel } from "@/components/CallsPanel";
import { NotesFeed } from "@/components/NotesFeed";
import { HotToggle } from "@/components/HotToggle";
import { DialBar, MobileCallBar } from "@/components/DialBar";
import { NoteComposer } from "@/components/NoteComposer";
import { PhoneLink } from "@/components/PhoneLink";
import { NoteItem } from "@/components/NoteItem";
import { OffersPanel, PricingCard } from "@/components/Offers";
import { callLog, lastCallPill, phonesIn } from "@/lib/callLog";
import { canEditNote } from "@/lib/notes";
import { useToast } from "@/components/Toast";
import {
  api,
  type DeadReason,
  type DealStrategy,
  type LeadDetail as LeadDetailData,
  type LeadStage,
  type ScField,
} from "@/lib/api";
import { STRATEGY_LABEL } from "@/lib/dealStages";
import {
  formatAcres,
  formatDate,
  formatDateTime,
  formatDue,
  formatMoneyCents,
  formatPhone,
  shown,
} from "@/lib/format";
import {
  DEAD_REASON_HELP,
  DEAD_REASON_LABEL,
  DEAD_REASONS,
  needsDeadReason,
  needsNextAction,
  REPLY_CLASSES,
  STAGE_HELP,
  STAGE_LABEL,
  stageTone,
} from "@/lib/stages";
import { cn } from "@/lib/utils";

// v2.1 T6.2 adds Engagements: work ordered while the land is still a lead.
const TABS = [
  "Messages",
  // v2.9 P1.2: the call log (cv_touches.lead_id) — it replaces v2.2's Calls tab, whose Voice list is
  // inside it.
  "Call log",
  "Notes",
  "Offers",
  "Tasks",
  "Parcels",
  "Engagements",
  "DD snapshot",
  "Timeline",
] as const;
type Tab = (typeof TABS)[number];

/**
 * SmarterContact's own panel grouping and order (§7.4).
 *
 * v2.1 T6.4: Contact and Property are gone from the mirror — the FACTS card owns them, editable and
 * override-protected, and showing the same facts twice (one copy editable, one not) is how a person
 * edits the wrong one. What stays is what FACTS does not carry, collapsed, with its count.
 */
const PANEL_GROUPS = ["smartercontact", "improvements", "followup"] as const;

const GROUP_LABEL: Record<string, string> = {
  smartercontact: "SmarterContact",
  improvements: "Improvements",
  followup: "Follow-up",
};

/** Render each value the way SC shows it, using the mapping's transform. */
function scValue(field: ScField): string {
  if (field.value === null || field.value === undefined || field.value === "") return "—";
  switch (field.kind) {
    case "money":
      return formatMoneyCents(Number(field.value));
    case "acres":
      return formatAcres(field.value);
    case "date":
      return formatDate(field.value);
    case "phone":
      return formatPhone(field.value);
    case "bool":
      return field.value === "true" || field.value === "True" ? "Yes" : "No";
    case "list":
      // Already comma-joined by the API.
      return field.value;
    default:
      return field.value;
  }
}

function ScPanel({ data }: { data: LeadDetailData }) {
  const isVacant = data.lead.property_type === "vacant_land";
  return (
    <div className="space-y-3">
      {PANEL_GROUPS.map((group) => {
        // §7.4: house fields are hidden entirely on vacant land, rather than shown empty.
        if (group === "improvements" && isVacant) return null;
        const fields = data.sc_fields.filter((f) => f.group === group);
        if (fields.length === 0) return null;
        return (
          <details
            key={group}
            className="rounded-[var(--radius-card)] border bg-surface"
            data-testid={`sc-group-${group}`}
          >
            <summary className="cursor-pointer px-3 py-2 text-xs font-semibold tracking-wide text-fg-muted uppercase">
              {GROUP_LABEL[group] ?? group}{" "}
              <span className="tabular-nums font-normal">· {fields.length}</span>
            </summary>
            <dl className="space-y-1 px-3 pb-3 text-xs">
              {fields.map((f) => (
                <div key={f.target + f.label} className="flex gap-2" data-testid="sc-field">
                  <dt className="w-32 shrink-0 text-fg-muted" title={f.target}>
                    {f.label}
                  </dt>
                  <dd
                    className={cn(
                      "min-w-0 flex-1 break-words",
                      f.value ? "" : "text-fg-muted",
                    )}
                  >
                    {f.kind === "phone" && f.value ? (
                      <PhoneLink e164={f.value} leadId={data.lead.id} surface="lead-fields" />
                    ) : (
                      scValue(f)
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </details>
        );
      })}

      {Object.keys(data.unmapped).length > 0 && (
        <details className="rounded-[var(--radius-card)] border bg-surface" data-testid="sc-unmapped">
          <summary className="cursor-pointer px-3 py-2 text-xs font-semibold tracking-wide text-fg-muted uppercase">
            Unmapped SC fields{" "}
            <span className="tabular-nums font-normal">· {Object.keys(data.unmapped).length}</span>
          </summary>
          <dl className="space-y-1 px-3 text-xs">
            {Object.entries(data.unmapped).map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <dt className="w-32 shrink-0 text-fg-muted">{k}</dt>
                <dd className="min-w-0 flex-1 break-words">{String(v)}</dd>
              </div>
            ))}
          </dl>
          <p className="px-3 pt-2 pb-3 text-[11px] text-fg-muted">
            Map these to columns from <Link to="/ingest" className="underline">Ingest</Link>.
          </p>
        </details>
      )}

      {(data.parse_errors.length > 0 || data.parse_warnings.length > 0) && (
        <Card title="Parse notes" data-testid="sc-parse-notes">
          <ul className="space-y-1 text-[11px]">
            {data.parse_errors.map((e) => (
              <li key={e} className="text-danger">
                {e}
              </li>
            ))}
            {data.parse_warnings.map((w) => (
              <li key={w} className="text-warn">
                {w}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {data.lead.sc_contact_url && (
        <a
          href={data.lead.sc_contact_url}
          target="_blank"
          rel="noreferrer"
          data-testid="open-in-sc"
          className="block rounded border px-2 py-1.5 text-center text-xs hover:bg-surface-2"
        >
          Open in SmarterContact ↗
        </a>
      )}
    </div>
  );
}

function StageControl({
  data,
  deadArmed,
  onDeadHandled,
}: {
  data: LeadDetailData;
  /** The `D` key arms the reason chips from the page, so the keyboard flow and the mouse agree. */
  deadArmed?: boolean;
  onDeadHandled?: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [stage, setStage] = useState<LeadStage>(data.lead.stage);
  const [reason, setReason] = useState<DeadReason | null>(data.lead.dead_reason ?? null);
  const [action, setAction] = useState(data.lead.next_action ?? "");
  const [due, setDue] = useState(
    data.lead.next_action_due ? data.lead.next_action_due.slice(0, 16) : "",
  );

  // `D` means "kill this lead": it selects the stage and shows the chips, and does not submit — the
  // reason is still a decision, and a hotkey that killed a lead in one keystroke would be a hotkey
  // nobody dares press.
  const dying = needsDeadReason(stage) || Boolean(deadArmed);
  const effectiveStage: LeadStage = deadArmed ? "dead" : stage;

  const save = useMutation({
    mutationFn: () =>
      api.patchLead(data.lead.id, {
        stage: effectiveStage,
        ...(needsDeadReason(effectiveStage) ? { dead_reason: reason } : {}),
        next_action: action || null,
        next_action_due: due ? new Date(due).toISOString() : null,
      }),
    onSuccess: () => {
      toast.push("Lead updated");
      onDeadHandled?.();
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  const revive = useMutation({
    mutationFn: () => api.reviveLead(data.lead.id),
    onSuccess: () => {
      toast.push("Back in Follow Up");
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  const requires = needsNextAction(effectiveStage);
  const blocked =
    (requires && !(action.trim() && due)) || (needsDeadReason(effectiveStage) && !reason);
  const deadHistory = data.dead_history ?? [];

  return (
    <Card title="Stage" data-testid="stage-control">
      <div className="space-y-3">
        <Field label="Stage">
          <select
            value={effectiveStage}
            onChange={(e) => {
              setStage(e.target.value as LeadStage);
              onDeadHandled?.();
            }}
            className={inputClass}
            data-testid="stage-select"
          >
            <option value={data.lead.stage}>{STAGE_LABEL[data.lead.stage]} (current)</option>
            {/* v2.3 P1.3: Signed is never hand-pickable — promote-to-deal is its only writer, and the
                API refuses it by hand. Shown below as disabled help instead of a choice that fails. */}
            {data.allowed_stages
              .filter((s) => s !== "signed")
              .map((s) => (
                <option key={s} value={s}>
                  {STAGE_LABEL[s]}
                </option>
              ))}
          </select>
          {data.allowed_stages.includes("signed") && (
            <p className="mt-1 text-[11px] text-fg-muted" aria-disabled="true" data-testid="stage-signed-help">
              {STAGE_LABEL.signed} — promote to deal (below)
            </p>
          )}
          <p className="mt-1 text-[11px] text-fg-muted" data-testid="stage-help">
            {STAGE_HELP[effectiveStage]}
          </p>
        </Field>

        {dying && (
          <Field label="Reason" required>
            {/* One chip, required. `dead` with no reason is the CRM forgetting rather than recording,
                and `unsubscribed` feeds the §12 suppression export (D-31). */}
            <div className="flex flex-wrap gap-1.5" data-testid="dead-reason-chips">
              {DEAD_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  title={DEAD_REASON_HELP[r]}
                  aria-pressed={reason === r}
                  data-testid={`dead-reason-${r}`}
                  onClick={() => setReason(r)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-xs",
                    reason === r
                      ? "border-danger bg-danger/15 font-medium text-danger"
                      : "text-fg-muted hover:bg-surface-2",
                  )}
                >
                  {DEAD_REASON_LABEL[r]}
                </button>
              ))}
            </div>
            {reason && <p className="mt-1 text-[11px] text-fg-muted">{DEAD_REASON_HELP[reason]}</p>}
          </Field>
        )}

        <Field label="Next action" required={requires}>
          <input
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className={inputClass}
            placeholder={requires ? "Required for this stage" : "Optional"}
            data-testid="detail-next-action"
          />
        </Field>
        <Field label="Due" required={requires}>
          <input
            type="datetime-local"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            className={inputClass}
            data-testid="detail-next-action-due"
          />
        </Field>
        {blocked && (
          <p className="text-[11px] text-warn" data-testid="next-action-warning">
            {needsDeadReason(effectiveStage) && !reason
              ? "Dead needs one of the five reasons."
              : `${STAGE_LABEL[effectiveStage]} needs a next action and a due date.`}
          </p>
        )}
        {/* Disabled ONLY when a dead reason is missing.
            A missing next action deliberately still submits: the API is the authority on §6.1 and its
            422 is what the operator should see, and a button that silently refuses to be pressed
            teaches nothing about why. A missing dead REASON is different — the request would carry
            `dead_reason: null`, which is a field the UI can see is empty before asking. */}
        <Button
          variant="primary"
          className="w-full"
          disabled={save.isPending || (needsDeadReason(effectiveStage) && !reason)}
          onClick={() => save.mutate()}
          data-testid="save-stage"
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>

        {data.lead.stage === "dead" && (
          <>
            <Button
              className="w-full"
              disabled={revive.isPending}
              onClick={() => revive.mutate()}
              data-testid="revive-lead"
            >
              {revive.isPending ? "Reviving…" : "Revive to Follow Up"}
            </Button>
            <p className="text-[11px] text-fg-muted">
              A revive moves the reason into this lead's history and puts it back on Today. The seller can
              also revive it themselves: any new inbound message does the same thing (§4.4).
            </p>
          </>
        )}
        {deadHistory.length > 0 && (
          <p className="text-[11px] text-fg-muted" data-testid="dead-history">
            Died and came back {deadHistory.length} time{deadHistory.length === 1 ? "" : "s"}:{" "}
            {deadHistory.map((h) => deadHistoryReason(h)).join(", ")}
          </p>
        )}
      </div>
    </Card>
  );
}

function TaskComposer({ leadId }: { leadId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [due, setDue] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 16);
  });

  const create = useMutation({
    mutationFn: () =>
      api.createTask({
        entity_type: "lead",
        entity_id: leadId,
        title,
        due_at: due ? new Date(due).toISOString() : null,
        priority: "p2",
        notes: null,
      }),
    onSuccess: () => {
      toast.push("Task created");
      setTitle("");
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <div className="min-w-48 flex-1">
        <Field label="New task">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Pull the CAD card"
            className={inputClass}
            data-testid="task-title"
          />
        </Field>
      </div>
      <Field label="Due">
        <input
          type="datetime-local"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          className={inputClass}
          data-testid="task-due"
        />
      </Field>
      <Button
        type="submit"
        variant="primary"
        disabled={!title.trim() || create.isPending}
        data-testid="task-create"
      >
        Add
      </Button>
    </form>
  );
}

/** §7.4 "Promote to deal". §6.1 wants a signed PSA, or an explicit override with a note. */
function PromoteToDeal({ lead }: { lead: LeadDetailData }) {
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [strategy, setStrategy] = useState<DealStrategy>("deed_split");
  const [price, setPrice] = useState(String(lead.asking_price_cents ?? ""));
  const [override, setOverride] = useState(false);
  const [note, setNote] = useState("");

  const hasDeal = lead.lead.deal_count > 0;

  const promote = useMutation({
    mutationFn: () =>
      api.promoteLead(lead.lead.id, {
        name: null,
        strategy,
        contract_price_cents: price ? Number(price) : null,
        emd_cents: null,
        option_period_days: null,
        contract_date: null,
        override_psa: override,
        override_note: note || null,
      }),
    onSuccess: (deal) => {
      toast.push(`Deal created with ${deal.checklist.length} checklist items`);
      void qc.invalidateQueries();
      navigate(`/deals/${deal.deal.id}`);
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  if (hasDeal) {
    return (
      <Card title="Deal">
        <p className="text-xs text-fg-muted">
          This lead already has a deal.{" "}
          <Link to="/deals" className="underline">
            Open the pipeline
          </Link>
          .
        </p>
      </Card>
    );
  }

  return (
    <Card title="Promote to deal" data-testid="promote-card">
      {!open ? (
        <Button className="w-full" onClick={() => setOpen(true)} data-testid="promote-open">
          Promote to deal
        </Button>
      ) : (
        <div className="space-y-2">
          <Field label="Strategy">
            <select
              value={strategy}
              onChange={(e) => setStrategy(e.target.value as DealStrategy)}
              className={inputClass}
              data-testid="promote-strategy"
            >
              {Object.entries(STRATEGY_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Contract price (cents)">
            <input
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              inputMode="numeric"
              className={inputClass + " tabular-nums"}
              data-testid="promote-price"
            />
          </Field>
          <label className="flex items-start gap-2 text-[11px] text-fg-muted">
            <input
              type="checkbox"
              checked={override}
              onChange={(e) => setOverride(e.target.checked)}
              data-testid="promote-override"
              className="mt-0.5"
            />
            <span>
              No signed PSA uploaded yet — override with a note (§6.1 requires one or the other).
            </span>
          </label>
          {override && (
            <Field label="Why" required>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className={inputClass}
                data-testid="promote-note"
              />
            </Field>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              disabled={promote.isPending || (override && !note.trim())}
              onClick={() => promote.mutate()}
              data-testid="promote-submit"
            >
              {promote.isPending ? "Creating…" : "Create deal"}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/** One entry of `dead_history`, as a reason word. The column is jsonb, so nothing about it is typed. */
function deadHistoryReason(entry: unknown): string {
  if (entry && typeof entry === "object" && "reason" in entry) {
    const reason = (entry as { reason?: unknown }).reason;
    if (typeof reason === "string" && reason) return reason;
  }
  return "unknown";
}

/** §7.4 lead detail, three columns. */
export function LeadDetail() {
  const { leadId } = useParams<{ leadId: string }>();
  const [tab, setTab] = useState<Tab>("Messages");
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [noteBody, setNoteBody] = useState("");
  // `D` arms the reason chips in the Stage card; `E` focuses the first editable fact.
  const [deadArmed, setDeadArmed] = useState(false);
  const focusFirstFact = useRef<(() => void) | null>(null);
  const noteInput = useRef<HTMLTextAreaElement | null>(null);
  const focusCallLog = useRef<(() => void) | null>(null);
  const [phoneOffer, setPhoneOffer] = useState<string[]>([]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["lead", leadId],
    queryFn: () => api.lead(leadId!),
    enabled: Boolean(leadId),
  });

  // Auto-advance after a decision. Accepting, rejecting or killing a lead is the end of working it, so
  // the next thing anybody wants is the next lead — going back to the list to click it is the friction
  // the keyboard flow exists to remove (D-32). The advance is silent when there is nowhere to go.
  const advance = useCallback(() => {
    const list = params.get("q");
    const index = Number(params.get("i") ?? 0);
    if (!list) return;
    // The list lives in `useLeadNav`, inside the nav bar. Rather than lift that query up here just to
    // read one id, the index is stepped and the SAME lead page is re-rendered at the new position — the
    // nav bar then resolves which lead that is and this component follows it. A stale index self-corrects
    // because `useLeadNav` prefers the position it actually finds over the one in the URL.
    navigate(`/leads/${leadId}?q=${encodeURIComponent(list)}&i=${index + 1}`);
  }, [params, navigate, leadId]);

  const decide = useMutation({
    mutationFn: (verdict: "accept" | "reject") =>
      verdict === "accept"
        ? api.acceptSuggestion(leadId!)
        : api.rejectSuggestion(leadId!, "rejected from the keyboard flow"),
    onSuccess: (_d, verdict) => {
      toast.push(verdict === "accept" ? "Suggestion applied" : "Suggestion rejected");
      void qc.invalidateQueries();
      advance();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => api.me() });

  // v2.2 P3.1: the flame in the header and H.
  const toggleHot = useMutation({
    mutationFn: (hot: boolean) => api.patchLead(leadId!, { hot }),
    onSuccess: (_d, hot) => {
      toast.push(hot ? "Marked hot" : "Unmarked hot");
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  const addNote = useMutation({
    mutationFn: () =>
      api.createNote({ entity_type: "lead", entity_id: leadId!, body: noteBody, pinned: false }),
    onSuccess: () => {
      toast.push("Note added");
      // v2.9 P2.3: a number typed in the note is offered to the lead, inline.
      const known = new Set((data?.call_phones ?? []).map((p) => p.slice(-10)));
      setPhoneOffer(phonesIn(noteBody).filter((p) => !known.has(p.slice(-10))));
      setNoteBody("");
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  if (isLoading) return <Spinner label="Loading lead…" />;
  if (error) return <div className="p-4"><ErrorNote error={error} /></div>;
  if (!data) return null;

  const l = data.lead;
  const conflictCount = (data.conflicts ?? []).length;
  const name = l.entity_name ?? [l.first_name, l.last_name].filter(Boolean).join(" ");
  const killFlags = data.parcels.flatMap((p) => {
    const flags: string[] = [];
    if (p.legal_access === "no") flags.push("No legal access");
    if (p.floodplain_pct !== null && Number(p.floodplain_pct) > 50)
      flags.push(`Floodplain ${p.floodplain_pct}%`);
    return flags;
  });

  return (
    <div className="p-4 max-md:pb-48">
      <header className="mb-3">
        <div className="mb-2">
          <LeadNav
            leadId={l.id}
            actions={{
              onAccept:
                l.agent_suggestion_status === "pending" ? () => decide.mutate("accept") : undefined,
              onReject:
                l.agent_suggestion_status === "pending" ? () => decide.mutate("reject") : undefined,
              onDead: l.stage === "dead" ? undefined : () => setDeadArmed(true),
              onNote: () => {
                setTab("Notes");
                // The tab has to render before the input exists.
                requestAnimationFrame(() => noteInput.current?.focus());
              },
              onEdit: () => focusFirstFact.current?.(),
              onCancel: () => setDeadArmed(false),
              onHot: () => toggleHot.mutate(!(l.hot ?? false)),
              onLog: () => {
                setTab("Call log");
                requestAnimationFrame(() => focusCallLog.current?.());
              },
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to={l.stage === "dead" ? "/archived" : "/leads"} className="text-xs text-fg-muted hover:underline">
            {l.stage === "dead" ? "Archived" : "Leads"}
          </Link>
          <span className="text-xs text-fg-muted">/</span>
          <h1 className="text-lg font-semibold tracking-tight" data-testid="lead-name">
            {name || "(unnamed)"}
          </h1>
          <HotToggle
            hot={l.hot ?? false}
            size="md"
            onToggle={(hot) => toggleHot.mutate(hot)}
            disabled={toggleHot.isPending}
            testId="lead-hot"
          />
          <Badge tone={stageTone(l.stage)} title={STAGE_HELP[l.stage]}>
            {STAGE_LABEL[l.stage]}
          </Badge>
          {l.dead_reason && (
            <Badge tone="danger" title={DEAD_REASON_HELP[l.dead_reason]}>
              {DEAD_REASON_LABEL[l.dead_reason]}
            </Badge>
          )}
          {conflictCount > 0 && (
            <Badge tone="warn" data-testid="conflict-chip">
              {conflictCount} conflict{conflictCount === 1 ? "" : "s"}
            </Badge>
          )}
          {(l.tags ?? []).map((tag) => (
            <Badge key={tag} tone="muted">
              {tag.replace(/_/g, " ")}
            </Badge>
          ))}
          {lastCallPill(data.last_call) && (
            <OutcomePill outcome={data.last_call?.outcome} testId="lead-last-call">
              {lastCallPill(data.last_call)}
            </OutcomePill>
          )}
          {l.tier && <Badge tone="muted">Tier {l.tier}</Badge>}
          {l.stale && <Badge tone="warn">stale</Badge>}
        </div>
        {/* v2.10 P1.2 / P3.3: a big Dial (through Voice) and Text beside it. */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <DialBar owner={{ kind: "lead", id: l.id }} phones={data.call_phones ?? []} surface="lead-page" />
          {l.consent_status && l.consent_status !== "unknown" && (
            <Badge tone={l.consent_status === "opted_in" ? "ok" : "danger"}>
              {l.consent_status}
            </Badge>
          )}
        </div>
        <p className="mt-0.5 text-xs text-fg-muted">
          {[formatPhone(l.phone_e164), shown(l.market), shown(l.parcel_county), formatAcres(l.parcel_acres)]
            .filter((x) => x && x !== "—")
            .join(" · ")}
        </p>
      </header>

      {killFlags.length > 0 && (
        <div
          role="alert"
          data-testid="kill-banner"
          className="mb-3 rounded border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
        >
          <strong className="font-semibold">Kill check:</strong> {killFlags.join(" · ")}
        </div>
      )}

      {data.curative ? (
        <CurativeCard curative={data.curative} parcel={data.parcels[0]} />
      ) : null}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[18rem_minmax(0,1fr)_17rem]">
        <div className="space-y-3">
          {/* The editable facts come FIRST, above the read-only SC mirror. Whichever is on top is the
              one people reach for, and the one they should reach for is the one they can correct. */}
          <EditableFacts
            data={data}
            registerFocus={(focus) => {
              focusFirstFact.current = focus;
            }}
          />
          <ScPanel data={data} />
        </div>

        <div>
          <div className="mb-2 flex flex-wrap gap-1" role="tablist">
            {TABS.map((t) => (
              <button
                key={t}
                role="tab"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
                data-testid={`tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
                className={cn(
                  "rounded px-2 py-1 text-xs font-medium",
                  tab === t ? "bg-brand/15 text-brand" : "text-fg-muted hover:bg-surface-2",
                )}
              >
                {t}
              </button>
            ))}
          </div>

          {tab === "Messages" && (
            <Card title={`Messages (${data.messages.length}) — read-only mirror of SmarterContact`}>
              {data.messages.length === 0 ? (
                <Empty title="No message history imported." />
              ) : (
                <ul className="space-y-2">
                  {data.messages.map((m) => (
                    <li
                      key={m.id}
                      className={cn(
                        "max-w-[80%] rounded border px-2 py-1.5 text-xs",
                        m.direction === "inbound" ? "bg-surface-2" : "ml-auto bg-brand/10",
                      )}
                    >
                      <div className="mb-0.5 flex items-center gap-2 text-[11px] text-fg-muted">
                        <span>{m.sender ?? m.direction}</span>
                        <span>{formatDateTime(m.sent_at)}</span>
                      </div>
                      <p className="whitespace-pre-wrap">{m.body}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          {tab === "Call log" && (
            <Card title={`Call log (${(data.touches ?? []).length})`} data-testid="lead-call-log">
              <CallLogPanel
                owner={{ kind: "lead", id: l.id }}
                entries={data.touches ?? []}
                phones={data.call_phones ?? []}
                canEdit={(entry) => me?.role === "owner" || (entry.logged_by_email ?? "") === me?.email}
                onChanged={() => void qc.invalidateQueries()}
                focusRef={focusCallLog}
              />
              {/* v2.2 P4.5's Calls tab, inside the call log: Voice's own record of every call, text and
                  voicemail on this lead's numbers, with playback and the voicemail suggestion. */}
              <details open className="mt-3 border-t pt-2" data-testid="calls-tab">
                <summary className="cursor-pointer text-xs font-medium">Google Voice: calls, texts and voicemails</summary>
                <div className="mt-2">
                  <CallsPanel leadId={l.id} />
                </div>
              </details>
            </Card>
          )}

          {tab === "Notes" && (
            <Card title={`Notes (${data.notes.filter((n) => n.note_class !== "system").length})`}>
              {/* v2.10 P5.1 (D-138): one composer — Enter adds, Shift+Enter is a new line. */}
              <NoteComposer
                className="mb-3"
                testId="note"
                inputRef={noteInput}
                value={noteBody}
                onChange={setNoteBody}
                busy={addNote.isPending}
                onSubmit={() => addNote.mutate()}
              />
              {phoneOffer.length > 0 && (
                <div className="mb-2 flex flex-wrap items-center gap-2 rounded border border-accent/30 bg-accent/5 px-2 py-1 text-xs" data-testid="note-phone-offer">
                  <span>That note has {phoneOffer.map(formatPhone).join(", ")}.</span>
                  <Button
                    size="sm"
                    data-testid="note-phone-save"
                    onClick={() =>
                      void callLog.savePhones({ kind: "lead", id: l.id }, phoneOffer).then(
                        () => {
                          toast.push("Saved to the lead");
                          setPhoneOffer([]);
                          void qc.invalidateQueries();
                        },
                        (e: Error) => toast.push(e.message, "danger"),
                      )
                    }
                  >
                    Save phone to card
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setPhoneOffer([])}>
                    No
                  </Button>
                </div>
              )}
              <NotesFeed
                notes={data.notes}
                callCount={(data.touches ?? []).length}
                renderNote={(n) => (
                  <NoteItem
                    key={n.id}
                    note={n}
                    editable={canEditNote(n, me)}
                    pinned={n.pinned}
                    badges={n.pinned ? <Badge tone="brand">pinned</Badge> : undefined}
                    onSave={(body) => api.updateNote(n.id, { body }).then(() => qc.invalidateQueries())}
                    onDelete={() => api.deleteNote(n.id).then(() => qc.invalidateQueries())}
                    onRestore={() =>
                      api.updateNote(n.id, { deleted_at: null }).then(() => qc.invalidateQueries())
                    }
                    onPin={(pinned) => callLog.pinNote("lead", n.id, pinned).then(() => qc.invalidateQueries())}
                    onConvert={() => callLog.convertNote("lead", n.id).then(() => qc.invalidateQueries())}
                    onSavePhones={(phones) =>
                      callLog.savePhones({ kind: "lead", id: l.id }, phones).then(() => qc.invalidateQueries())
                    }
                  />
                )}
                renderCalls={() => (
                  <CallLogPanel
                    owner={{ kind: "lead", id: l.id }}
                    entries={data.touches ?? []}
                    phones={data.call_phones ?? []}
                    canEdit={() => me?.role === "owner"}
                    onChanged={() => void qc.invalidateQueries()}
                  />
                )}
              />
            </Card>
          )}

          {tab === "Offers" && <OffersPanel lead={data} />}

          {tab === "Tasks" && (
            <Card title={`Tasks (${data.tasks.length})`}>
              <div className="mb-3">
                <TaskComposer leadId={l.id} />
              </div>
              {data.tasks.length === 0 ? (
                <Empty title="No tasks on this lead." />
              ) : (
                <ul className="space-y-1" data-testid="lead-tasks">
                  {data.tasks.map((t) => (
                    <li key={t.id} className="flex items-center gap-2 rounded border px-2 py-1.5 text-xs">
                      <span className={cn("flex-1", t.done_at && "text-fg-muted line-through")}>
                        {t.title}
                      </span>
                      <Badge tone={t.priority === "p1" ? "danger" : "muted"}>{t.priority}</Badge>
                      <span className="tabular-nums text-fg-muted">{formatDue(t.due_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          {tab === "Parcels" && (
            <Card title={`Parcels (${data.parcels.length})`}>
              {data.parcels.map((p) => (
                <div key={p.id} className="mb-3 rounded border p-2 text-xs last:mb-0">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="font-medium">{p.apn ?? "(no APN)"}</span>
                    <Badge tone="muted">{p.property_type}</Badge>
                    <span className="text-fg-muted">
                      {p.county} {p.state} · {formatAcres(p.acres)}
                    </span>
                  </div>
                  <p className="text-fg-muted">{p.situs_display ?? p.situs_raw ?? "—"}</p>
                  <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
                    <dt className="text-fg-muted">Zoning</dt>
                    <dd>{p.zoning ?? "—"}</dd>
                    <dt className="text-fg-muted">Legal access</dt>
                    <dd>{p.legal_access}</dd>
                    <dt className="text-fg-muted">Floodplain</dt>
                    <dd>{p.floodplain_pct === null ? "—" : `${p.floodplain_pct}%`}</dd>
                    <dt className="text-fg-muted">CAD land value</dt>
                    <dd className="tabular-nums">{formatMoneyCents(p.cad_land_value_cents)}</dd>
                  </dl>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {p.google_maps_url && (
                      <a className="underline" href={p.google_maps_url} target="_blank" rel="noreferrer">
                        Google Maps
                      </a>
                    )}
                    {p.gis_url && (
                      <a className="underline" href={p.gis_url} target="_blank" rel="noreferrer">
                        County GIS
                      </a>
                    )}
                    {p.regrid_url && (
                      <a className="underline" href={p.regrid_url} target="_blank" rel="noreferrer">
                        Regrid
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          )}

          {tab === "Engagements" && <LeadEngagements leadId={l.id} />}

          {tab === "DD snapshot" && <DdSnapshotTab lead={data} />}

          {tab === "Timeline" && (
            <Card title={`Timeline (${data.events.length})`}>
              <ul className="space-y-1" data-testid="timeline">
                {data.events.map((e) => (
                  <li key={e.id} className="rounded border px-2 py-1.5 text-xs" data-testid="timeline-event">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={e.event_type === "stage_changed" ? "info" : "muted"}>
                        {e.event_type}
                      </Badge>
                      <span className="text-fg-muted">{e.entity_type}</span>
                      <span className="text-fg-muted">{e.actor}</span>
                      <span className="ml-auto tabular-nums text-fg-muted">
                        {formatDateTime(e.occurred_at)}
                      </span>
                    </div>
                    {(e.from_value ?? e.to_value) && (
                      <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words text-[11px] text-fg-muted">
                        {JSON.stringify(e.from_value)} → {JSON.stringify(e.to_value)}
                      </pre>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        <div className="space-y-3">
          <StageControl
            data={data}
            deadArmed={deadArmed}
            onDeadHandled={() => setDeadArmed(false)}
          />

          <AutoAppliedPanel leadId={l.id} />

          <PricingCard lead={data} />

          <Card title="Triage">
            <dl className="space-y-1 text-xs">
              <div className="flex justify-between gap-2">
                <dt className="text-fg-muted">Score</dt>
                <dd className="tabular-nums">{l.motivation_score}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-fg-muted">Reply class</dt>
                <dd>{l.reply_class ?? "—"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-fg-muted">Heirs / estate</dt>
                <dd>{data.heirs_estate ? "yes" : "no"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-fg-muted">Follow up on</dt>
                <dd>{formatDate(data.follow_up_on)}</dd>
              </div>
            </dl>
            <p className="mt-2 text-[11px] text-fg-muted">
              Reply classes: {REPLY_CLASSES.length} values, kept verbatim from the SMS agent.
            </p>
          </Card>

          <PromoteToDeal lead={data} />

          <AgentSuggestion leadId={l.id} stage={l.stage} />
        </div>
      </div>
      {/* v2.10 P4.2: on a phone, Dial, Text and the quick-log chips stay in a sticky bottom bar. */}
      <MobileCallBar owner={{ kind: "lead", id: l.id }} phones={data.call_phones ?? []} />
    </div>
  );
}
